import { useEffect, useSyncExternalStore } from 'react';
import { ApiError } from '../../lib/apiClient';
import type { FontRead } from '../../types/api';
import { listFonts } from './fontsApi';

/**
 * The font catalogue, fetched once per session and shared by every screen
 * that draws a community design: the studio's inspector and canvas, the
 * gallery tiles, the brief's pin and the review stage all read the same copy.
 *
 * - `loading`: the one request is in flight.
 * - `ready`: `fonts` is the server's admitted list, in its order.
 * - `unavailable`: the server has no `/fonts` route yet (a 404). Held for the
 *   session — a reload after the backend ships picks the catalogue up.
 * - `error`: anything else. The next caller that asks tries again.
 */
export type FontCatalogStatus = 'idle' | 'loading' | 'ready' | 'unavailable' | 'error';

export interface FontCatalog {
  status: FontCatalogStatus;
  fonts: readonly FontRead[];
  bySlug: ReadonlyMap<string, FontRead>;
}

const EMPTY: readonly FontRead[] = [];

let snapshot: FontCatalog = { status: 'idle', fonts: EMPTY, bySlug: new Map() };
let inFlight = false;
const listeners = new Set<() => void>();

function publish(next: FontCatalog): void {
  snapshot = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): FontCatalog {
  return snapshot;
}

/**
 * Starts the one fetch unless it has already settled for the session or is
 * under way. Safe to call from every mount; it is a no-op after the first.
 * Not aborted on unmount: the answer belongs to the session, not a screen.
 */
export function ensureFontCatalog(): void {
  if (inFlight || snapshot.status === 'ready' || snapshot.status === 'unavailable') return;
  inFlight = true;
  publish({ ...snapshot, status: 'loading' });
  listFonts()
    .then((fonts) => {
      publish({
        status: 'ready',
        fonts,
        bySlug: new Map(fonts.map((font) => [font.slug, font])),
      });
    })
    .catch((caught: unknown) => {
      const missing = caught instanceof ApiError && caught.status === 404;
      publish({ status: missing ? 'unavailable' : 'error', fonts: EMPTY, bySlug: new Map() });
    })
    .finally(() => {
      inFlight = false;
    });
}

/**
 * The session's font catalogue. Pass `enabled = false` from a screen that only
 * needs it when a design names a font, so a map on the theme font never asks.
 */
export function useFontCatalog(enabled = true): FontCatalog {
  const catalog = useSyncExternalStore(subscribe, getSnapshot);
  useEffect(() => {
    if (enabled) ensureFontCatalog();
  }, [enabled]);
  return catalog;
}
